import React, {useState, useEffect} from 'react'
import {Input} from 'reactstrap'
import ImagePromptInput from '../ImagePromptInput'
import CreditInfo from './CreditInfo'
import {loadGenerateBackgroundConfig} from '../../../../../../../services/GenerateBackgroundServices'
import {getAccessToken} from '../../../../../../../services/AuthServices'
import {getLinksUploadBackground, uploadTempArtwork} from '../../../../../../../services/ArtworkTemplateServices'
import {toaster} from '../../../../../../../shared/PFToast'

const InputSection = ({
    promptText,
    setPromptText,
    attachedImages,
    setAttachedImages,
    onChangeResolution,
    imageCount,
    setImageCount,
    selectedResolution,
    fileSizeError,
    setFileSizeError,
    tokenInfo,
    tokenWarningMessage,
}) => {
    const [config, setConfig] = useState(null)
    const [isLoggedIn, setIsLoggedIn] = useState(false)
    const [isUploadingImage, setIsUploadingImage] = useState(false)

    useEffect(() => {
        const loadConfig = async () => {
            const loadedConfig = await loadGenerateBackgroundConfig()
            setConfig(loadedConfig)
        }
        loadConfig()
    }, [])

    useEffect(() => {
        // Check if user is logged in by checking access token
        const accessToken = getAccessToken()
        setIsLoggedIn(!!accessToken)
    }, [])

    const handleOnClickLoginNow = () => {
        // Navigate to login page REACT_APP_BASE_URL/login
        window.location.href = `${process.env.REACT_APP_BASE_URL}/login`
    }

    /**
     * Upload image file to storage and get the image URL
     * @param {File} imageFile - The image file to upload
     * @returns {Promise<string>} - The image URL after upload
     */
    const uploadImageAndGetUrl = async (imageFile) => {
        try {
            // Get presigned URL for upload
            const {success, data: presignedUrlData} = await getLinksUploadBackground({limit: 1})
            
            if (!success || !presignedUrlData) {
                throw new Error('Failed to get presigned URL for image upload')
            }
            
            // Extract URL from response (response can be array or object with {url, key})
            const presignedUrlObj = Array.isArray(presignedUrlData) ? presignedUrlData[0] : presignedUrlData
            const presignedUrl = presignedUrlObj?.url || presignedUrlObj
            
            if (!presignedUrl || typeof presignedUrl !== 'string') {
                throw new Error('Invalid presigned URL format')
            }
            
            // Upload image to presigned URL
            await uploadTempArtwork(presignedUrl, imageFile, imageFile.type || 'image/jpeg')
            
            // Extract the image URL from presigned URL (remove query parameters)
            const imageUrl = presignedUrl.split('?')[0]
            
            return imageUrl
        } catch (error) {
            console.error('[InputSection] Failed to upload image:', error)
            throw error
        }
    }

    /**
     * Handle images change from ImagePromptInput
     * Upload file to storage and get link before saving to state
     */
    const handleImagesChange = async (newImages) => {
        if (!newImages || newImages.length === 0) {
            setAttachedImages([])
            setFileSizeError(false) // Clear error when removing images
            return
        }

        // Get the first image (only one image is allowed)
        const imageItem = newImages[0]
        
        // If image already has imageUrl, use it directly (already uploaded)
        if (imageItem.imageUrl) {
            setAttachedImages(newImages)
            setFileSizeError(false) // Clear error when using existing image
            return
        }

        // If image has file but no imageUrl, validate and upload it first
        if (imageItem.file) {
            // Validate file size against config maxFileSize
            const maxFileSize = config?.maxFileSize || 7 * 1024 * 1024 // Default 7MB in bytes
            const fileSize = imageItem.file.size
            
            if (maxFileSize && fileSize > maxFileSize) {
                // Show toast error
                toaster({
                    type: 'error',
                    message: `File size exceeds the maximum allowed size of ${Math.round(maxFileSize / (1024 * 1024))}MB`,
                    placement: 'top',
                })
                
                // Don't upload, clear the image
                setAttachedImages([])
                return
            }
            
            setIsUploadingImage(true)
            setFileSizeError(false) // Clear previous error
            try {
                const imageUrl = await uploadImageAndGetUrl(imageItem.file)
                
                // Update image item with imageUrl and keep previewUrl for display
                const updatedImage = {
                    ...imageItem,
                    imageUrl: imageUrl,
                }
                
                setAttachedImages([updatedImage])
                setFileSizeError(false) // Clear error on success
            } catch (error) {
                console.error('[InputSection] Failed to upload image:', error)
                // Store error state in image item to check later on generate
                const errorImage = {
                    ...imageItem,
                    uploadError: true,
                }
                setAttachedImages([errorImage])
                // Don't set fileSizeError here - only show error when clicking generate
            } finally {
                setIsUploadingImage(false)
            }
        } else {
            setAttachedImages(newImages)
        }
    }

    return (
        <>
            <div id="prompt-input-container">
                <CreditInfo
                    config={config}
                    isLoggedIn={isLoggedIn}
                    onLoginClick={handleOnClickLoginNow}
                    tokenInfo={tokenInfo}
                />

                <ImagePromptInput
                    className="ImagePromptInput"
                    value={promptText}
                    onChangeText={setPromptText}
                    images={attachedImages}
                    onChangeImages={handleImagesChange}
                    onFileSizeError={setFileSizeError}
                    disabled={isUploadingImage}
                    maxPromptLength={config?.maxPromptLength}
                    minPromptLength={config?.minPromptLength}
                />
                
                {fileSizeError && (
                    <div className="mt-2">
                        <small className="text-danger">
                            Only accepts files smaller than or equal to {config?.maxFileSize ? Math.round(config.maxFileSize / (1024 * 1024)) : 7}MB
                        </small>
                    </div>
                )}
                
                {!fileSizeError && promptText && promptText.trim().length > 0 && config?.minPromptLength && promptText.trim().length < config.minPromptLength && (
                    <div className="mt-2">
                        <small className="text-warning">
                            Please enter at least {config.minPromptLength} characters to generate background
                        </small>
                    </div>
                )}

                <div style={{marginTop: '16px'}}>
                    <div className="d-flex" style={{gap: '16px'}}>
                        <div style={{flex: '1 1 0', minWidth: 0}}>
                            <p className="mb-2">Output Image Resolution</p>
                            <div className="output-image-resolution-select">
                                <Input id="resolution-select" name="resolution" type="select" onChange={onChangeResolution}>
                                    {config && config.resolution && (
                                        config.resolution.map((res) => (
                                            <option key={res.value} value={res.value}>
                                                {res.label}
                                            </option>
                                        ))
                                    )}
                                </Input>
                            </div>
                        </div>
                        <div style={{flex: '1 1 0', minWidth: 0}}>
                            <p className="mb-2">Choose number of images to generate</p>
                            <div className="number-of-images-select">
                                <Input 
                                    id="image-count-select" 
                                    name="imageCount" 
                                    type="select" 
                                    value={imageCount || '2'}
                                    onChange={(e) => setImageCount && setImageCount(e.target.value)}
                                >
                                    <option value="1">1 image</option>
                                    <option value="2">2 images</option>
                                    <option value="3">3 images</option>
                                    <option value="4">4 images</option>
                                </Input>
                            </div>
                            {tokenWarningMessage && (
                                <div className="mt-2">
                                    <small className="text-danger">
                                        {tokenWarningMessage}
                                    </small>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default InputSection

